import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { TicketUpdated } from '@/types/ticket';
import { ticketService } from '@/api/services/ticketService';
import { HttpClient } from '@/api/services/HttpClient';
import type { RootState } from '@/store/store';
import { upsertTickets, addNewTicket } from '../tickets/ticketsSlice';

type ViewType = "all" | "unassigned" | "my-inbox" | `team-${string}` | `bot-${string}`;

interface PageData {
    ids: string[];
    fetched: boolean;
}

interface ViewData {
    pages: Record<number, PageData>;
    totalTickets: number;
}

interface TicketPagesState {
    views: Record<string, ViewData>; // Changed to string to support dynamic team views
    pageSize: number;
    isLoading: boolean;
    isError: boolean;
}

const initialState: TicketPagesState = {
    views: {
        all: { pages: {}, totalTickets: 0 },
        unassigned: { pages: {}, totalTickets: 0 },
        "my-inbox": { pages: {}, totalTickets: 0 }
    },
    pageSize: 20,
    isLoading: false,
    isError: false,
};

export const fetchTicketsForView = createAsyncThunk(
    'ticketPages/fetchTicketsForView',
    async (params: { page: number, view: ViewType, ticketId?: string }, { dispatch, rejectWithValue }) => {
        try {
            const response = await ticketService.getTicketsByPage({
                page: params.page,
                limit: 20,
                sortOrder: "desc",
                view: params.view,
                ticketId: params.ticketId
            });

            // Store tickets in the normalized tickets slice
            dispatch(upsertTickets(response.tickets));

            return {
                view: params.view,
                page: params.page,
                ticketIds: response.tickets.map(t => t.id),
                totalTicketCount: response.totalTicketCount
            };
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const fetchTeamTickets = createAsyncThunk(
    'ticketPages/fetchTeamTickets',
    async (params: { teamId: string, page: number }, { dispatch, rejectWithValue, getState }) => {
        try {
            const state = getState() as RootState;
            const view = `team-${params.teamId}`;

            // Check if we already have data for this team cached
            const existingView = state.ticketPages.views[view];
            const pageSize = 20;

            if (existingView && existingView.totalTickets > 0) {
                // We have cached data, just return the requested page
                const allTicketIds = Object.values(existingView.pages)
                    .sort((a, b) => Object.keys(existingView.pages).indexOf(a as any) - Object.keys(existingView.pages).indexOf(b as any))
                    .flatMap(page => page.ids);

                const startIndex = (params.page - 1) * pageSize;
                const endIndex = startIndex + pageSize;
                const pageTicketIds = allTicketIds.slice(startIndex, endIndex);

                return {
                    view,
                    page: params.page,
                    ticketIds: pageTicketIds,
                    totalTicketCount: existingView.totalTickets,
                    fromCache: true
                };
            }

            // Fetch fresh data from API
            const response = await HttpClient.apiClient.get(`/team/${params.teamId}/team-tickets`);

            if (response.data.status !== 'success') {
                throw new Error('Failed to fetch team tickets');
            }

            // Convert API tickets to TicketUpdated format
            const convertedTickets: TicketUpdated[] = response.data.data.map((apiTicket: any) => ({
                id: apiTicket.id || '',
                sno: apiTicket.sno || 0,
                subject: apiTicket.title || '',
                title: apiTicket.title || '',
                lastMessage: apiTicket.lastMessage || '',
                assignee: apiTicket.assignee?.name || null,
                assigneeId: apiTicket.assigneeId || null,
                company: apiTicket.customer?.name || '',
                tags: apiTicket.tags || [],
                status: apiTicket.status || 'open',
                priority: apiTicket.priority || 'medium',
                createdAt: apiTicket.createdAt || new Date().toISOString(),
                updatedAt: apiTicket.updatedAt || new Date().toISOString(),
                customerId: apiTicket.customerId || '',
                customer: apiTicket.customer || '',
                teamId: apiTicket.teamId || '',
                csatDaysRemaining: apiTicket.csatDaysRemaining ?? null,
                permanentCloseDaysRemaining: apiTicket.permanentCloseDaysRemaining ?? null,
                hasNotification: false,
                isUnread: false,
                notificationType: null,
                threadId: apiTicket.id || '',
                externalId: apiTicket.id || ''
            }));

            // Store all tickets in the normalized tickets slice
            dispatch(upsertTickets(convertedTickets));

            // Return paginated data for the requested page
            const startIndex = (params.page - 1) * pageSize;
            const endIndex = startIndex + pageSize;
            const paginatedTickets = convertedTickets.slice(startIndex, endIndex);

            return {
                view,
                page: params.page,
                ticketIds: paginatedTickets.map(t => t.id),
                totalTicketCount: convertedTickets.length,
                allTickets: convertedTickets.map(t => t.id) // Include all ticket IDs for caching
            };
        } catch (error) {
            console.error('Error fetching team tickets:', error);
            return rejectWithValue(error);
        }
    }
);

export const fetchBotTickets = createAsyncThunk(
    'ticketPages/fetchBotTickets',
    async (params: { botId: string, page: number }, { dispatch, rejectWithValue, getState }) => {
        try {
            const state = getState() as RootState;
            const view = `bot-${params.botId}`;

            // Check if we already have data for this bot cached
            const existingView = state.ticketPages.views[view];
            const pageSize = 20;

            if (existingView && existingView.totalTickets > 0) {
                // We have cached data, just return the requested page
                const allTicketIds = Object.values(existingView.pages)
                    .sort((a, b) => Object.keys(existingView.pages).indexOf(a as any) - Object.keys(existingView.pages).indexOf(b as any))
                    .flatMap(page => page.ids);

                const startIndex = (params.page - 1) * pageSize;
                const endIndex = startIndex + pageSize;
                const pageTicketIds = allTicketIds.slice(startIndex, endIndex);

                return {
                    view,
                    page: params.page,
                    ticketIds: pageTicketIds,
                    totalTicketCount: existingView.totalTickets,
                    fromCache: true
                };
            }

            // Fetch fresh data from API using the new ticket inbox endpoint structure
            let response;
            try {
                // Try specific bot endpoint with new structure
                response = await HttpClient.apiClient.get(`/ticket/inbox/bot/${params.botId}`);
            } catch (error) {
                // Fallback to general bot endpoint
                response = await HttpClient.apiClient.get('/ticket/bot');
            }

            if (response.data.status !== 'success') {
                throw new Error('Failed to fetch bot tickets');
            }

            // Filter tickets by botId if we're using the general endpoint
            const apiData = response.data?.data;
            let ticketsData: any[] = [];

            if (Array.isArray(apiData)) {
                ticketsData = apiData;
            } else if (Array.isArray(apiData?.data)) {
                ticketsData = apiData.data;
            } else if (apiData?.tickets && Array.isArray(apiData.tickets)) {
                ticketsData = apiData.tickets;
            } else if (Array.isArray(response.data?.tickets)) {
                ticketsData = response.data.tickets;
            }

            // If we have a botId field in the tickets, filter by it
            if (Array.isArray(ticketsData) && ticketsData.length > 0 && ticketsData[0].botId) {
                ticketsData = ticketsData.filter((ticket: any) => ticket.botId === params.botId);
            }

            // Convert API tickets to TicketUpdated format
            const convertedTickets: TicketUpdated[] = ticketsData.map((apiTicket: any) => ({
                id: apiTicket.id || '',
                sno: apiTicket.sno || 0,
                subject: apiTicket.title || apiTicket.subject || '',
                title: apiTicket.title || apiTicket.subject || '',
                lastMessage: apiTicket.lastMessage || '',
                assignee: apiTicket.assignee?.name || null,
                assigneeId: apiTicket.assigneeId || null,
                company: apiTicket.customer?.name || apiTicket.company || '',
                tags: apiTicket.tags || [],
                status: apiTicket.status || 'open',
                priority: apiTicket.priority || 'medium',
                createdAt: apiTicket.createdAt || new Date().toISOString(),
                updatedAt: apiTicket.updatedAt || new Date().toISOString(),
                customerId: apiTicket.customerId || '',
                customer: apiTicket.customer || '',
                csatDaysRemaining: apiTicket.csatDaysRemaining ?? null,
                permanentCloseDaysRemaining: apiTicket.permanentCloseDaysRemaining ?? null,
                botId: params.botId, // Add bot ID to identify bot tickets
                hasNotification: false,
                isUnread: false,
                notificationType: null,
                threadId: apiTicket.id || '',
                externalId: apiTicket.id || ''
            }));

            // Store all tickets in the normalized tickets slice
            dispatch(upsertTickets(convertedTickets));

            // Return paginated data for the requested page
            const startIndex = (params.page - 1) * pageSize;
            const endIndex = startIndex + pageSize;
            const paginatedTickets = convertedTickets.slice(startIndex, endIndex);

            return {
                view,
                page: params.page,
                ticketIds: paginatedTickets.map(t => t.id),
                totalTicketCount: typeof apiData?.totalCount === 'number' ? apiData.totalCount : convertedTickets.length,
                allTickets: convertedTickets.map(t => t.id) // Include all ticket IDs for caching
            };
        } catch (error) {
            console.error('Error fetching bot tickets:', error);
            return rejectWithValue(error);
        }
    }
);

const ticketPagesSlice = createSlice({
    name: 'ticketPages',
    initialState,
    reducers: {
        markPageAsFetched: (state, action: PayloadAction<{ view: ViewType, page: number, ticketIds: string[] }>) => {
            const { view, page, ticketIds } = action.payload;
            if (!state.views[view].pages[page]) {
                state.views[view].pages[page] = { ids: [], fetched: false };
            }
            state.views[view].pages[page].ids = ticketIds;
            state.views[view].pages[page].fetched = true;
        },
        invalidateView: (state, action: PayloadAction<ViewType>) => {
            Object.keys(state.views[action.payload].pages).forEach(pageNum => {
                state.views[action.payload].pages[parseInt(pageNum)].fetched = false;
            });
        },
        clearView: (state, action: PayloadAction<ViewType>) => {
            state.views[action.payload] = { pages: {}, totalTickets: 0 };
        },
        addNewTicketToViews: (state, action: PayloadAction<{ ticket: TicketUpdated; currentUserId?: string }>) => {
            const { ticket, currentUserId } = action.payload;
            const pageSize = state.pageSize;

            // Determine which views this ticket belongs to
            const targetViews: ViewType[] = ["all"];

            if (!ticket.assignedTo || ticket.assignedTo === null) {
                targetViews.push("unassigned");
            } else if (currentUserId && ticket.assignedTo === currentUserId) {
                targetViews.push("my-inbox");
            }

            // Add ticket to appropriate views
            targetViews.forEach(view => {
                const viewData = state.views[view];
                if (!viewData) {
                    return;
                }

                // Remove ticket from all pages within this view to avoid duplicates
                let ticketWasPresent = false;
                Object.values(viewData.pages).forEach(page => {
                    if (!page?.ids?.length) {
                        return;
                    }
                    if (page.ids.includes(ticket.id)) {
                        ticketWasPresent = true;
                        page.ids = page.ids.filter(id => id !== ticket.id);
                    }
                });

                // Invalidate all pages except page 1 for this view
                Object.keys(viewData.pages).forEach(pageNum => {
                    const pageNumber = parseInt(pageNum);
                    if (pageNumber > 1) {
                        viewData.pages[pageNumber].fetched = false;
                    }
                });

                // Handle page 1
                if (!viewData.pages[1]) {
                    viewData.pages[1] = { ids: [], fetched: true };
                }

                // Add ticket to beginning of page 1
                viewData.pages[1].ids.unshift(ticket.id);

                // If page 1 exceeds pageSize, remove the last item
                if (viewData.pages[1].ids.length > pageSize) {
                    viewData.pages[1].ids.pop();
                }

                // Increment total count
                if (!ticketWasPresent) {
                    viewData.totalTickets += 1;
                }
            });
        },
        bumpTicketToTopOfViews: (state, action: PayloadAction<{ ticket: TicketUpdated; currentUserId?: string }>) => {
            const { ticket, currentUserId } = action.payload;

            // Determine which views this ticket belongs to
            const targetViews: ViewType[] = ["all"];

            if (!ticket.assignedTo || ticket.assignedTo === null) {
                targetViews.push("unassigned");
            } else if (currentUserId && ticket.assignedTo === currentUserId) {
                targetViews.push("my-inbox");
            }

            // Bump ticket to top in appropriate views
            targetViews.forEach(view => {
                // Check all pages to find and remove the ticket
                let ticketFound = false;
                Object.keys(state.views[view].pages).forEach(pageNum => {
                    const pageNumber = parseInt(pageNum);
                    const page = state.views[view].pages[pageNumber];
                    if (page && page.ids.includes(ticket.id)) {
                        page.ids = page.ids.filter(id => id !== ticket.id);
                        ticketFound = true;
                    }
                });

                // If ticket was found, invalidate pages beyond page 1
                if (ticketFound) {
                    Object.keys(state.views[view].pages).forEach(pageNum => {
                        const pageNumber = parseInt(pageNum);
                        if (pageNumber > 1) {
                            state.views[view].pages[pageNumber].fetched = false;
                        }
                    });
                }

                // Ensure page 1 exists
                if (!state.views[view].pages[1]) {
                    state.views[view].pages[1] = { ids: [], fetched: true };
                }

                // Add ticket to beginning of page 1 if not already there
                if (!state.views[view].pages[1].ids.includes(ticket.id)) {
                    state.views[view].pages[1].ids.unshift(ticket.id);
                }
            });
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTicketsForView.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
            })
            .addCase(fetchTicketsForView.fulfilled, (state, action) => {
                const { view, page, ticketIds, totalTicketCount } = action.payload;
                state.isLoading = false;

                if (!state.views[view]) {
                    state.views[view] = { pages: {}, totalTickets: 0 };
                }
                if (!state.views[view].pages[page]) {
                    state.views[view].pages[page] = { ids: [], fetched: false };
                }

                state.views[view].pages[page].ids = ticketIds;
                state.views[view].pages[page].fetched = true;
                state.views[view].totalTickets = totalTicketCount;
            })
            .addCase(fetchTicketsForView.rejected, (state) => {
                state.isLoading = false;
                state.isError = true;
            })
            .addCase(fetchTeamTickets.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
            })
            .addCase(fetchTeamTickets.fulfilled, (state, action) => {
                const { view, page, ticketIds, totalTicketCount, allTickets } = action.payload;
                state.isLoading = false;

                if (!state.views[view]) {
                    state.views[view] = { pages: {}, totalTickets: 0 };
                }

                // If this is the first fetch (we have allTickets), populate all pages
                if (allTickets) {
                    const pageSize = 20;
                    const totalPages = Math.ceil(totalTicketCount / pageSize);

                    // Clear existing pages
                    state.views[view].pages = {};

                    // Populate all pages with data
                    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
                        const startIndex = (pageNum - 1) * pageSize;
                        const endIndex = startIndex + pageSize;
                        const pageTicketIds = allTickets.slice(startIndex, endIndex);

                        state.views[view].pages[pageNum] = {
                            ids: pageTicketIds,
                            fetched: true
                        };
                    }
                } else {
                    // Just update the requested page (from cache)
                    if (!state.views[view].pages[page]) {
                        state.views[view].pages[page] = { ids: [], fetched: false };
                    }
                    state.views[view].pages[page].ids = ticketIds;
                    state.views[view].pages[page].fetched = true;
                }

                state.views[view].totalTickets = totalTicketCount;
            })
            .addCase(fetchTeamTickets.rejected, (state) => {
                state.isLoading = false;
                state.isError = true;
            })
            .addCase(fetchBotTickets.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
            })
            .addCase(fetchBotTickets.fulfilled, (state, action) => {
                const { view, page, ticketIds, totalTicketCount, allTickets } = action.payload;
                state.isLoading = false;

                if (!state.views[view]) {
                    state.views[view] = { pages: {}, totalTickets: 0 };
                }

                // If this is the first fetch (we have allTickets), populate all pages
                if (allTickets) {
                    const pageSize = 20;
                    const totalPages = Math.ceil(totalTicketCount / pageSize);

                    // Clear existing pages
                    state.views[view].pages = {};

                    // Populate all pages with data
                    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
                        const startIndex = (pageNum - 1) * pageSize;
                        const endIndex = startIndex + pageSize;
                        const pageTicketIds = allTickets.slice(startIndex, endIndex);

                        state.views[view].pages[pageNum] = {
                            ids: pageTicketIds,
                            fetched: true
                        };
                    }
                } else {
                    // Just update the requested page (from cache)
                    if (!state.views[view].pages[page]) {
                        state.views[view].pages[page] = { ids: [], fetched: false };
                    }
                    state.views[view].pages[page].ids = ticketIds;
                    state.views[view].pages[page].fetched = true;
                }

                state.views[view].totalTickets = totalTicketCount;
            })
            .addCase(fetchBotTickets.rejected, (state) => {
                state.isLoading = false;
                state.isError = true;
            })
            .addCase(addNewTicket, (state, action) => {
                // This will be handled by the addNewTicketToViews action
                // but we listen to it here to trigger the view updates
            });
    },
});

// Selectors
export const selectViewPages = (view: string) => (state: RootState) =>
    state.ticketPages.views[view]?.pages || {};

export const selectViewTotalTickets = (view: string) => (state: RootState) =>
    state.ticketPages.views[view]?.totalTickets || 0;

export const selectPageTicketIds = (view: string, page: number) => (state: RootState) =>
    state.ticketPages.views[view]?.pages[page]?.ids || [];

export const selectIsPageFetched = (view: string, page: number) => (state: RootState) =>
    state.ticketPages.views[view]?.pages[page]?.fetched || false;

export const selectPagesLoading = (state: RootState) => state.ticketPages.isLoading;
export const selectPagesError = (state: RootState) => state.ticketPages.isError;

// Combined selector to get actual ticket objects for a view/page
export const selectTicketsForViewPage = (view: string, page: number) => (state: RootState) => {
    const ticketIds = state.ticketPages.views[view]?.pages[page]?.ids || [];
    return ticketIds.map(id => state.ticket.byId[id]).filter(Boolean);
};

export const { markPageAsFetched, invalidateView, clearView, addNewTicketToViews, bumpTicketToTopOfViews } = ticketPagesSlice.actions;
export const ticketPagesReducer = ticketPagesSlice.reducer;
